import { createLocaleUtilities } from '@atabix/vue-ui/src/lib/i18n/LocaleUtilities.ts'
import axios from 'axios'
import dayjs from 'dayjs'

import { initializeI18n, setI18nLocale } from '@/plugins/i18n'
import { useSettingsStore } from '@/stores'

export class LocaleService {
  // NOTE: The fallback locale is not really used, the backend always sends the locale to use.
  // This is mostly here because i18n needs a fallback locale.
  public static readonly FALLBACK_LOCALE = 'nl-NL'
  public static readonly FALLBACK_LANGUAGE = 'nl'

  /**
   * Previously we had 2 letter language codes, those were stored in localstore.
   * This mapping is used to convert the legacy codes to new locale codes.
   */
  private static readonly LEGACY_LOCALES: Record<string, string> = {
    nl: 'nl-NL',
    en: 'en-GB',
    de: 'de-DE',
  }

  public static readonly utils = createLocaleUtilities(LocaleService.FALLBACK_LOCALE)

  private static initialized = false

  public static async initialize(): Promise<void> {
    if (this.initialized) {
      return
    }

    if (LocaleService.getLocale() !== LocaleService.FALLBACK_LOCALE) {
      await this.onLocaleChange()
    }
    this.initialized = true
  }

  public static getSupportedLocales(): string[] {
    const languages = useSettingsStore().get('languages')
    return languages?.map((lang) => lang.code) ?? [LocaleService.FALLBACK_LOCALE]
  }

  public static async setAppLocale(locale: string | undefined, fromStore?: boolean): Promise<boolean> {
    if (!LocaleService.utils.validateLocale(locale, true)) return false

    if (useSettingsStore().locale === locale) {
      return true
    }

    if (!fromStore) {
      useSettingsStore().setLocale(locale)

      await useSettingsStore().teapot()
      await useSettingsStore().refresh()
    }
    return true
  }

  public static getClientLocale(): string {
    return useSettingsStore().locale ?? LocaleService.utils.getUrlParamLocale() ?? LocaleService.utils.getBrowserLocale() ?? ''
  }

  public static getLocale(): string {
    return useSettingsStore().get('language') ?? LocaleService.FALLBACK_LOCALE
  }

  public static async onLocaleChange(): Promise<void> {
    const locale = LocaleService.getLocale()
    const language = LocaleService.utils.getLanguage(locale)

    LocaleService.utils.updateHtmlLocale(locale)
    axios.defaults.headers.common['Accept-Language'] = LocaleService.getClientLocale()
    setI18nLocale(language)
    dayjs.locale(language)
  }

  public static migrateLocale(locale: string): string {
    if (!LocaleService.utils.validateLocale(locale)) {
      // eslint-disable-next-line security/detect-object-injection
      return LocaleService.LEGACY_LOCALES[locale]
    }
    return locale
  }
}
